<template>
  <div class="lg:w-1/2 px-16">
    <h3 class="text-h4">{{ sectionTitle }}</h3>
    <template v-for="(card, index) in visibleCards">
      <h3 v-if="card.isSectionHeader" :key="`header-${index}`" class="text-grey-header text-overline mt-32 mb-16">
        {{ card.title }}
      </h3>
      <ImageLinkCard
        v-else
        :key="`card-${index}`"
        :class="card.className"
        :title="card.title"
        :content="card.content"
        :elevation="card.elevation"
        :link-u-r-l="card.linkURL"
        :to="card.to"
        :use-full-page-navigation="card.useFullPageNavigation"
        :clickable="card.clickable"
      >
        <template #rightSide>
          <DisclosureIcon class="mr-0 ml-16" aria-hidden="true" />
        </template>
      </ImageLinkCard>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { RawLocation } from 'vue-router';
import ImageLinkCard from '@/components/image_link_card/ImageLinkCard.vue';
import DisclosureIcon from '@/graphics/icons/DisclosureIcon.vue';
import { CareType } from '@/types/care_type';

defineOptions({ name: 'ProfileCompleteActions' });

const props = defineProps<{
  isAdditionalProfile: boolean;
  hasPhoto: boolean;
  photoProfileType: CareType;
  formattedCareType: string;
  jobSearchLink: string;
  profileEditLink: string;
}>();

interface ActionCard {
  title: string;
  content?: string;
  elevation?: number;
  linkURL?: string;
  to?: RawLocation;
  useFullPageNavigation?: boolean;
  clickable?: boolean;
  className?: string;
  condition?: () => boolean;
  isSectionHeader?: boolean;
}

const sectionTitle = computed(() =>
  props.isAdditionalProfile ? 'Take these steps to get hired faster' : 'Take the first step to land a job'
);
const addProfilePhotoCard = computed<ActionCard>(() => ({
  title: 'Add a profile photo',
  content: 'Putting a face to your name helps build trust with families',
  elevation: 2,
  to: { name: 'provider-photo-upload', query: { profileType: props.photoProfileType } },
  clickable: true,
  className: 'mt-16',
  condition: () => !props.hasPhoto,
}));
const standardProfileCards = computed<ActionCard[]>(() => [
  addProfilePhotoCard.value,
  { isSectionHeader: true, title: 'Other ways to get started' },
  {
    title: 'Apply to 10 or more jobs',
    content: 'Sitters who apply to 10 or more jobs are 4x more likely to get hired',
    elevation: 1,
    linkURL: props.jobSearchLink,
    useFullPageNavigation: true,
    clickable: true,
    className: 'mt-16',
  },
  {
    title: 'Make yourself stand out',
    content: 'Personalize your profile with your education, references, and more',
    elevation: 1,
    linkURL: props.profileEditLink,
    useFullPageNavigation: false,
    clickable: true,
    className: 'mt-16',
  },
]);
const additionalProfileCards = computed<ActionCard[]>(() => [
  addProfilePhotoCard.value,
  {
    title: `Start applying to ${props.formattedCareType} jobs`,
    content: 'Apply to at least 5 jobs to increase your visibility with families looking for this service',
    elevation: 2,
    linkURL: props.jobSearchLink,
    useFullPageNavigation: true,
    clickable: true,
    className: 'mt-16',
  },
  {
    title: 'Highlight your relevant experience',
    content: 'Customize your profile with skills and experience specific to this care type to attract more families',
    elevation: 1,
    linkURL: props.profileEditLink,
    useFullPageNavigation: false,
    clickable: true,
    className: 'mt-16',
  },
]);
const visibleCards = computed(() => {
  const cards = props.isAdditionalProfile ? additionalProfileCards.value : standardProfileCards.value;
  return cards.filter((card) => card.isSectionHeader || !card.condition || card.condition());
});
</script>
